import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut } from 'lucide-react';
import { ROUTES } from '@/utils/constants';
import { isIntelligencePath } from '@/utils/intelligence';
import { NavGroup } from './NavGroup';
import { useAuthStore } from '@/store/authStore';
import { useOnboardingStore } from '@/store/onboardingStore';
import { Logo } from '@/components/ui/Logo';
import { SeverityChip } from '@/components/ui/SeverityChip';
import { APP_VERSION, GIT_SHA } from '@/generated/version';

/**
 * Mono glyph used as the nav item marker.
 * Active items render it in terminal green; inactive in muted fg-3.
 */
const NAV_GLYPH = '><>';

interface NavItemProps {
  to: string;
  label: string;
  /** Optional critical count chip (e.g. live detections) */
  badge?: number;
  /** Override the default prefix match, for routes that don't share a path prefix. */
  isActive?: (pathname: string) => boolean;
}

/**
 * Single sidebar nav item — 38px tall, mono 13px, 2px green left border + green
 * glyph when active (per the "Sidebar nav item" recipe).
 */
function NavItem({ to, label, badge, isActive: isActiveFn }: NavItemProps): React.ReactElement {
  const location = useLocation();
  const isActive = isActiveFn
    ? isActiveFn(location.pathname)
    : location.pathname === to || location.pathname.startsWith(`${to}/`);

  return (
    <NavLink
      to={to}
      className={`flex items-center gap-3 h-[38px] px-3 w-full text-left font-mono text-[13px]
        rounded-phish-0 border-l-2 transition-colors duration-phish ${
          isActive
            ? 'bg-phish-bg-3 text-phish-fg-1 border-l-phish-accent'
            : 'bg-transparent text-phish-fg-2 border-l-transparent hover:bg-white/5'
        }`}
    >
      <span className={isActive ? 'text-phish-accent' : 'text-phish-fg-3'} aria-hidden>
        {NAV_GLYPH}
      </span>
      <span className="flex-1">{label}</span>
      {badge !== undefined && badge > 0 && (
        <SeverityChip
          sev="critical"
          label={badge > 99 ? '99+' : String(badge)}
          className="h-5 px-1.5"
        />
      )}
    </NavLink>
  );
}

interface SectionLabelProps {
  children: React.ReactNode;
}

/** Mono uppercase section label. */
function SectionLabel({ children }: SectionLabelProps): React.ReactElement {
  return (
    <div className="px-3 pt-5 pb-2 font-mono text-[11px] uppercase tracking-phish-label text-phish-fg-3">
      {children}
    </div>
  );
}

interface SubNavItemProps {
  to: string;
  label: string;
}

/** Indented sub-item used inside a NavGroup (Intelligence, DMARC) and the profile menu. */
function SubNavItem({ to, label }: SubNavItemProps): React.ReactElement {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <NavLink
      to={to}
      className={`flex items-center gap-2 h-[34px] pl-9 pr-3 w-full text-left font-mono text-[13px]
        rounded-phish-0 border-l-2 transition-colors duration-phish ${
          isActive
            ? 'bg-phish-bg-3 text-phish-fg-1 border-l-phish-accent'
            : 'bg-transparent text-phish-fg-2 border-l-transparent hover:bg-white/5'
        }`}
    >
      <span className={isActive ? 'text-phish-accent' : 'text-phish-fg-3'} aria-hidden>
        ▷
      </span>
      <span className="flex-1">{label}</span>
    </NavLink>
  );
}

interface SidebarProps {
  className?: string;
}

/**
 * Main sidebar navigation — terminal-on-OLED reskin.
 * 248px wide, bg-1 surface, hairline right border, mono nav, accent active state.
 */
export function Sidebar({ className = '' }: SidebarProps): React.ReactElement {
  const navigate = useNavigate();
  const { user, clearAuth } = useAuthStore();
  const {
    isComplete: onboardingComplete,
    hasLoadedInitialData: onboardingLoaded,
    getProgressPercentage,
  } = useOnboardingStore();

  // Accordion / alert state. The DMARC + Intelligence groups own their own open state
  // inside NavGroup.
  const [profileOpen, setProfileOpen] = useState(false);
  const [onboardingAlertOpen, setOnboardingAlertOpen] = useState(true);
  const showOnboardingReminder = onboardingLoaded && !onboardingComplete && onboardingAlertOpen;

  const userName = user?.full_name || 'User';
  const userEmail = user?.email || 'user@company.com';
  const userInitials = userName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const isAdmin = user?.role?.toLowerCase() === 'admin';
  const isPlatformAdmin = user?.role?.toLowerCase() === 'platform_admin';

  const handleLogout = () => {
    clearAuth();
    navigate(ROUTES.LOGIN);
  };

  return (
    <div
      className={`h-full w-full max-w-[248px] flex flex-col bg-phish-bg-1 border-r border-phish-border shadow-none ${className}`}
    >
      {/* Brand lockup */}
      <div className="px-4 py-5 border-b border-phish-border">
        <Logo variant="product" size="md" />
        <div className="mt-1.5 font-mono text-[11px] text-phish-fg-3">
          catch your phish in the sea
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-2">
        {/* Profile accordion */}
        <button
          type="button"
          onClick={() => setProfileOpen(!profileOpen)}
          className={`flex items-center gap-3 h-[52px] px-3 w-full text-left font-mono
            border-l-2 transition-colors duration-phish ${
              profileOpen
                ? 'bg-phish-bg-3 text-phish-fg-1 border-l-phish-accent'
                : 'bg-transparent text-phish-fg-2 border-l-transparent hover:bg-white/5'
            }`}
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-phish bg-phish-accent text-phish-bg-0 text-[12px] font-mono font-bold">
            {userInitials}
          </span>
          <span className="mr-auto min-w-0">
            <span className="block truncate text-[13px] text-phish-fg-1">{userName}</span>
            <span className="block truncate text-[11px] text-phish-fg-3">{userEmail}</span>
          </span>
          <ChevronDown
            size={16}
            strokeWidth={1.5}
            className={`text-phish-fg-3 transition-transform ${profileOpen ? 'rotate-180' : ''}`}
          />
        </button>
        {profileOpen && (
          <div className="py-1">
            <SubNavItem to={ROUTES.PROFILE} label="Profile" />
            <SubNavItem to={ROUTES.SETTINGS_GDPR} label="Privacy & Data" />
            {isPlatformAdmin && (
              <SubNavItem to={ROUTES.ADMIN_COMPANIES} label="Admin Console" />
            )}
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center gap-2 h-[34px] pl-9 pr-3 w-full text-left font-mono text-[13px]
                rounded-phish-0 border-l-2 border-l-transparent bg-transparent text-phish-fg-2
                hover:bg-white/5 transition-colors duration-phish"
            >
              <LogOut size={14} strokeWidth={1.5} className="text-phish-fg-3" />
              <span className="flex-1">Sign Out</span>
            </button>
          </div>
        )}

        <SectionLabel>Monitor</SectionLabel>
        <NavItem to={ROUTES.DASHBOARD} label="Dashboard" />
        <NavItem to={ROUTES.DETECTIONS} label="Detections" />
        {/* Campaigns is the correlation layer — it combines every intel signal and is
            what drives takedowns, so it sits directly above the step it feeds rather
            than behind a menu. */}
        <NavItem to={ROUTES.CAMPAIGNS} label="Campaigns" />
        <NavItem to={ROUTES.TAKEDOWNS} label="Takedowns" />

        {/* Standing intel — browsed occasionally, so it collapses behind one entry.
            `isIntelligencePath` also covers the dossier routes, which live at top level. */}
        <NavGroup label="Intelligence" isActive={isIntelligencePath}>
          <SubNavItem to={ROUTES.INTELLIGENCE} label="Overview" />
          <SubNavItem to={ROUTES.BOTNETS} label="Botnets" />
          <SubNavItem to={ROUTES.ACTORS} label="Threat actors" />
          <SubNavItem to={ROUTES.KITS} label="Phishing kits" />
        </NavGroup>

        <NavGroup label="DMARC" isActive={(p) => p.startsWith('/dmarc')}>
          <SubNavItem to={ROUTES.DMARC_REPORTS} label="Reports" />
          <SubNavItem to={ROUTES.DMARC_ANALYSIS} label="Analysis" />
          <SubNavItem to={ROUTES.DMARC_UNAUTHORIZED} label="Unauthorized" />
        </NavGroup>

        <NavItem to={ROUTES.TRAPS} label="Honeytokens" />
        <NavItem to={ROUTES.SERVICES} label="Services" />

        <SectionLabel>Company</SectionLabel>
        <NavItem to={ROUTES.USERS} label="Users" />
        <NavItem to={ROUTES.COMPANY_SETTINGS} label="Settings" />
        {isAdmin && <NavItem to={ROUTES.COMPANY_GDPR_SETTINGS} label="Company Privacy" />}
        {isAdmin && <NavItem to={ROUTES.COMPANY_INTEGRATIONS} label="Integrations" />}
      </div>

      {/* Onboarding reminder */}
      {showOnboardingReminder && (
        <div className="mx-3 mb-2 border border-phish-high/40 bg-phish-bg-2 rounded-phish p-3">
          <div className="flex items-start justify-between gap-2">
            <span className="font-mono text-[11px] uppercase tracking-phish-label text-phish-high">
              Complete setup
            </span>
            <button
              type="button"
              onClick={() => setOnboardingAlertOpen(false)}
              className="font-mono text-[11px] text-phish-fg-3 hover:text-phish-fg-1"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
          <p className="mt-1 font-sans text-[12px] text-phish-fg-2">
            {getProgressPercentage()}% complete — finish setup to enable all features.
          </p>
          <button
            type="button"
            onClick={() => navigate(ROUTES.ONBOARDING)}
            className="mt-2 font-mono text-[12px] text-phish-accent hover:underline"
          >
            ▷ Resume setup
          </button>
        </div>
      )}

      {/* Footer */}
      <div className="px-4 py-3 border-t border-phish-border">
        <div className="font-mono text-[11px] text-phish-fg-3">
          <span className="text-phish-accent">watch active</span>
          <span className="phish-cursor mx-0.5" />
          <span className="text-phish-fg-3"> / 2.4M domains/day</span>
        </div>
        {/* Build identity from src/generated/version.ts (scripts/gen-version.mjs).
            Was hardcoded "v3.11.3 / 37cf57c", which named a commit from months ago. */}
        <div className="mt-1 flex items-center gap-2 font-mono text-[10px] text-phish-fg-3">
          <span title="3.<branches>.<commits>">v{APP_VERSION}</span>
          <span title="Build commit">{GIT_SHA}</span>
        </div>
      </div>
    </div>
  );
}

export default Sidebar;
